import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useClients } from "../../hooks/useClients";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { toE164Digits, pickClientDisplayPhone } from "../../utils/phone";
import ClientSearchOption from "../clients/ClientSearchOption";

function pickClientName(client) {
  if (!client || typeof client !== "object") return "";
  return String(client.nome || client.name || "").trim();
}

function pickClientPhone(client) {
  return pickClientDisplayPhone(client);
}

function pickClientEmail(client) {
  if (!client || typeof client !== "object") return "";
  const em = client.email != null ? String(client.email).trim() : "";
  return em;
}

function pickClientCompany(client) {
  if (!client || typeof client !== "object") return "";
  return String(client.empresa || client.company || client.nome_empresa || "").trim();
}

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   onConfirm: (contact: { name: string, phone: string, email?: string|null, company?: string|null }) => void,
 *   projectId?: number|string|null,
 *   sending?: boolean,
 * }} props
 */
export default function ShareContactDialog({
  open,
  onClose,
  onConfirm,
  projectId = null,
  sending = false,
}) {
  const { t } = useTranslation("chat");
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");

  const debouncedSearch = useDebouncedValue(clientSearch, 400);
  const pid = projectId != null && Number(projectId) > 0 ? Number(projectId) : null;

  const { data: clientsData, isLoading: loadingClients } = useClients(
    1,
    15,
    debouncedSearch.trim(),
    pid ?? undefined,
    {
      enabled:
        open &&
        !!pid &&
        typeof debouncedSearch === "string" &&
        debouncedSearch.trim().length >= 2,
    }
  );

  const clientOptions = useMemo(() => {
    const list = Array.isArray(clientsData?.clients) ? clientsData.clients : [];
    return list;
  }, [clientsData]);

  useEffect(() => {
    if (!open) {
      setClientSearch("");
      setSelectedClient(null);
      setName("");
      setPhone("");
      setEmail("");
      setCompany("");
    }
  }, [open]);

  useEffect(() => {
    if (!selectedClient) return;
    setName(pickClientName(selectedClient));
    setPhone(pickClientPhone(selectedClient));
    setEmail(pickClientEmail(selectedClient));
    setCompany(pickClientCompany(selectedClient));
  }, [selectedClient]);

  const canSend = useMemo(() => {
    const nm = name.trim();
    const ph = phone.trim();
    const digits = toE164Digits(ph, "BR") || ph.replace(/\D/g, "");
    return !!nm && digits.length >= 8;
  }, [name, phone]);

  const handleConfirm = () => {
    if (!canSend || sending) return;
    onConfirm({
      name: name.trim(),
      phone: phone.trim(),
      email: email.trim() || null,
      company: company.trim() || null,
    });
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("shareContact.title")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("shareContact.hint")}
        </Typography>
        {pid ? (
          <Autocomplete
            sx={{ mb: 2 }}
            options={clientOptions}
            loading={loadingClients}
            value={selectedClient}
            onChange={(_, value) => setSelectedClient(value)}
            inputValue={clientSearch}
            onInputChange={(_, value) => setClientSearch(value)}
            getOptionLabel={(opt) =>
              opt ? `${pickClientName(opt) || "—"}${pickClientPhone(opt) ? ` · ${pickClientPhone(opt)}` : ""}` : ""
            }
            isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <ClientSearchOption
                  client={option}
                  primary={pickClientName(option)}
                  secondary={[pickClientEmail(option), pickClientPhone(option)]
                    .filter(Boolean)
                    .join(" · ")}
                />
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("shareContact.searchClient")}
                placeholder={t("shareContact.searchClientPlaceholder")}
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingClients ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        ) : null}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <TextField
            label={t("shareContact.name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            required
            fullWidth
            autoFocus={!pid}
          />
          <TextField
            label={t("shareContact.phone")}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            size="small"
            required
            fullWidth
          />
          <TextField
            label={t("shareContact.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label={t("shareContact.company")}
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            size="small"
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={sending}>
          {t("shareContact.cancel")}
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={!canSend || sending}>
          {sending ? t("shareContact.sending") : t("shareContact.send")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
